import {
  useSetTableMode,
  useTableMode,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import { SettingIcon } from '@/components/SettingIcon';
import { BlockTitle, List, ListItem, Navbar, Page, f7 } from 'framework7-react';
import { useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

type TableMode = 'SIMPLE' | 'ADVANCED';

function TablePlanPage() {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: tableModeView } = useTableMode();
  const setTableMode = useSetTableMode();
  const activeTableMode = tableModeView?.tableMode;

  const rs = t.reservationSetup;
  const MODE_INFO: Record<TableMode, { title: string; description: string; confirmMsg: string }> = {
    SIMPLE: {
      title: rs.simplesMode,
      description: rs.simpleModeTableDesc,
      confirmMsg: rs.switchSimple,
    },
    ADVANCED: {
      title: rs.advancedModeTable,
      description: rs.advancedModeTableDesc,
      confirmMsg: rs.switchAdvanced,
    },
  };

  const switchTableMode = (target: TableMode) => {
    if (target === activeTableMode) return;
    const info = MODE_INFO[target];
    f7.dialog.confirm(info.confirmMsg, info.title, () => {
      setTableMode.mutate(
        { tableMode: target },
        {
          onSuccess: () =>
            f7.toast.show({ text: rs.tableModeColon(info.title), closeTimeout: 2000 }),
          onError: (e) =>
            f7.dialog.alert(e instanceof Error ? e.message : rs.failedSwitchTableMode),
        },
      );
    });
  };

  return (
    <Page name="table-plan">
      <Navbar title={rs.tablePlan} backLink={settingsBackLink} />

      <BlockTitle>{rs.tableManagement}</BlockTitle>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {(['SIMPLE', 'ADVANCED'] as TableMode[]).map((tm) => {
          const isActive = tm === activeTableMode;
          const info = MODE_INFO[tm];
          return (
            <ListItem
              key={tm}
              link={setTableMode.isPending || !activeTableMode ? undefined : '#'}
              noChevron
              onClick={
                setTableMode.isPending || !activeTableMode ? undefined : () => switchTableMode(tm)
              }
            >
              <span slot="title">{info.title}</span>
              <span
                slot="subtitle"
                style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', display: 'block' }}
              >
                {info.description}
              </span>
              <i
                slot="after"
                className="f7-icons"
                style={{
                  color: isActive
                    ? 'var(--f7-theme-color)'
                    : 'var(--f7-list-item-subtitle-text-color)',
                  fontSize: 20,
                }}
              >
                {isActive ? 'checkmark_circle_fill' : 'circle'}
              </i>
            </ListItem>
          );
        })}
      </List>

      {activeTableMode === 'SIMPLE' && (
        <>
          <BlockTitle>{rs.manageTables}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={rs.tables} link="/settings/reservation/tables">
              <div slot="media">
                <SettingIcon icon="square_grid_2x2" color="#3b82f6" />
              </div>
            </ListItem>
          </List>
        </>
      )}

      {activeTableMode === 'ADVANCED' && (
        <>
          <BlockTitle>{rs.manageTables}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={rs.tablesFloorPlan} link="/settings/reservation/advanced-tables">
              <div slot="media">
                <SettingIcon icon="square_grid_2x2_fill" color="#3b82f6" />
              </div>
            </ListItem>
          </List>
        </>
      )}
    </Page>
  );
}

export default TablePlanPage;
